// Google Analytics 4 utility functions
import { hasAnalyticsConsent, respectDoNotTrack } from './consent';

// Type definitions for GA4 events
interface GAEvent {
  action: string;
  category?: string;
  label?: string;
  value?: number;
  [key: string]: any;
}

// Initialize gtag if not already present
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

/**
 * Track a page view
 */
export const trackPageView = (url: string, title?: string) => {
  if (!hasAnalyticsConsent() || respectDoNotTrack()) {
    console.log('📊 Analytics consent not given or DNT enabled, skipping page view');
    return;
  }
  
  if (typeof window.gtag === 'function') {
    window.gtag('config', window.GA_MEASUREMENT_ID || 'G-9W0T1ZH3JK', {
      page_path: url,
      page_title: title,
    });
    console.log('📊 Page view tracked:', url);
  }
};

/**
 * Track a custom event
 */
export const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
  if (!hasAnalyticsConsent()) {
    console.log('📊 Analytics consent not given, skipping event');
    return;
  }
  
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, parameters);
    console.log('📊 Event tracked:', eventName, parameters);
  }
};

/**
 * Set user properties
 */
export const setUserProperties = (properties: Record<string, any>) => {
  if (typeof window.gtag === 'function') {
    window.gtag('set', 'user_properties', properties);
    console.log('📊 User properties set:', properties);
  }
};

/**
 * Set user ID for tracking across sessions
 */
export const setUserId = (userId: string) => {
  if (typeof window.gtag === 'function') {
    window.gtag('config', window.GA_MEASUREMENT_ID || 'G-9W0T1ZH3JK', {
      user_id: userId,
    });
    console.log('📊 User ID set:', userId);
  }
};

// Authentication Events
export const trackSignupInitiated = () => {
  trackEvent('signup_initiated', {
    method: 'email',
    timestamp: Date.now(),
  });
};

export const trackSignupCompleted = (userId: string) => {
  trackEvent('signup_completed', {
    method: 'email',
    user_id: userId,
    timestamp: Date.now(),
  });
  setUserId(userId);
};

export const trackLoginInitiated = () => {
  trackEvent('login_initiated', {
    method: 'email',
    timestamp: Date.now(),
  });
};

export const trackLoginCompleted = (userId: string) => {
  trackEvent('login_completed', {
    method: 'email',
    user_id: userId,
    timestamp: Date.now(),
  });
  setUserId(userId);
};

export const trackLogout = (userId: string) => {
  trackEvent('logout', {
    user_id: userId,
    timestamp: Date.now(),
  });
};

// Designer Events
export const trackImageUploaded = (roomType: string, fileSize: number, userTier: string) => {
  trackEvent('image_uploaded', {
    room_type: roomType,
    file_size: fileSize,
    user_tier: userTier,
  });
};

export const trackRoomDetected = (detectedRoomType: string, confidence?: number) => {
  trackEvent('room_detected', {
    detected_room_type: detectedRoomType,
    confidence: confidence || 'unknown',
  });
};

export const trackStyleSelected = (styleName: string, styleCategory: string) => {
  trackEvent('style_selected', {
    style_name: styleName,
    style_category: styleCategory,
  });
};

export const trackDesignGenerationStarted = (
  roomType: string,
  style: string,
  userTier: string,
  designsRemaining: number
) => {
  trackEvent('design_generation_started', {
    room_type: roomType,
    style: style,
    user_tier: userTier,
    designs_remaining: designsRemaining,
  });
};

export const trackDesignGenerationCompleted = (
  roomType: string,
  style: string,
  generationTime: number,
  variationsCount: number
) => {
  trackEvent('design_generation_completed', {
    room_type: roomType,
    style: style,
    generation_time: generationTime,
    variations_count: variationsCount,
  });
};

export const trackDesignGenerationFailed = (
  errorType: string,
  roomType: string,
  style: string
) => {
  trackEvent('design_generation_failed', {
    error_type: errorType,
    room_type: roomType,
    style: style,
  });
};

export const trackProjectSaved = (projectId: string, userTier: string, saveType: 'manual' | 'auto') => {
  trackEvent('project_saved', {
    project_id: projectId,
    user_tier: userTier,
    save_type: saveType,
  });
};

export const trackProjectLoaded = (projectId: string, projectAgeDays: number) => {
  trackEvent('project_loaded', {
    project_id: projectId,
    project_age_days: projectAgeDays,
  });
};

export const trackDesignDownloaded = (designIndex: number, hasWatermark: boolean, userTier: string) => {
  trackEvent('design_downloaded', {
    design_index: designIndex,
    has_watermark: hasWatermark,
    user_tier: userTier,
  });
};

export const trackDownloadAll = (variationCount: number, userTier: string) => {
  trackEvent('download_all', {
    variation_count: variationCount,
    user_tier: userTier,
  });
};

// Pricing & Conversion Events
export const trackPricingPageViewed = (currentTier: string, designsUsed: number, designsRemaining: number) => {
  trackEvent('pricing_page_viewed', {
    current_tier: currentTier,
    designs_used: designsUsed,
    designs_remaining: designsRemaining,
  });
};

export const trackPlanClicked = (
  planName: string,
  planPrice: number,
  currentTier: string,
  currency: string,
  region: string
) => {
  trackEvent('plan_clicked', {
    plan_name: planName,
    plan_price: planPrice,
    current_tier: currentTier,
    currency: currency,
    region: region,
  });
};

export const trackUpgradeInitiated = (
  fromTier: string,
  toTier: string,
  price: number,
  currency: string
) => {
  trackEvent('upgrade_initiated', {
    from_tier: fromTier,
    to_tier: toTier,
    price: price,
    currency: currency,
  });
};

export const trackPotentialLeadLogged = (
  planInterested: string,
  userAuthenticated: boolean,
  region: string,
  country: string
) => {
  trackEvent('potential_lead_logged', {
    plan_interested: planInterested,
    user_authenticated: userAuthenticated,
    region: region,
    country: country,
  });
};

// Outbound social link clicks (footer, etc.)
export const trackSocialClick = (
  network: string,
  url: string,
  location: string = 'footer'
) => {
  trackEvent('social_link_click', {
    network,
    url,
    location,
  });
};

// Project Management Events
export const trackProjectsPageViewed = (projectCount: number, userTier: string) => {
  trackEvent('projects_page_viewed', {
    project_count: projectCount,
    user_tier: userTier,
  });
};

export const trackProjectCardClicked = (projectId: string, projectAgeDays: number) => {
  trackEvent('project_card_clicked', {
    project_id: projectId,
    project_age_days: projectAgeDays,
  });
};

export const trackProjectDeleted = (projectId: string, userTier: string) => {
  trackEvent('project_deleted', {
    project_id: projectId,
    user_tier: userTier,
  });
};

// Settings & Admin Events
export const trackSettingsViewed = () => {
  trackEvent('settings_viewed');
};

export const trackProfileUpdated = (fieldsChanged: string[]) => {
  trackEvent('profile_updated', {
    fields_changed: fieldsChanged.join(','),
  });
};

export const trackAdminPanelAccessed = (adminUserId: string) => {
  trackEvent('admin_panel_accessed', {
    admin_user_id: adminUserId,
  });
};

export const trackAdminTabChanged = (tabName: string) => {
  trackEvent('admin_tab_changed', {
    tab_name: tabName,
  });
};

// Landing Page Events
export const trackCTAClicked = (buttonName: string, location: string) => {
  trackEvent('cta_clicked', {
    button_name: buttonName,
    location: location,
  });
};

// Error Events
export const trackErrorOccurred = (
  errorType: string,
  errorMessage: string,
  page: string,
  userTier?: string
) => {
  trackEvent('error_occurred', {
    error_type: errorType,
    error_message: errorMessage,
    page: page,
    user_tier: userTier || 'unknown',
  });
};

export const trackApiCallFailed = (
  functionName: string,
  errorCode: string,
  userTier: string
) => {
  trackEvent('api_call_failed', {
    function_name: functionName,
    error_code: errorCode,
    user_tier: userTier,
  });
};

// E-commerce Events (for future payment integration)
export const trackBeginCheckout = (planName: string, price: number, currency: string) => {
  trackEvent('begin_checkout', {
    currency: currency,
    value: price,
    items: [{
      item_id: planName,
      item_name: planName,
      price: price,
    }],
  });
};

export const trackPurchase = (
  transactionId: string,
  planName: string,
  price: number,
  currency: string
) => {
  trackEvent('purchase', {
    transaction_id: transactionId,
    value: price,
    currency: currency,
    items: [{
      item_id: planName,
      item_name: planName,
      price: price,
    }],
  });
};

// Legal & Utility Page Events
export const trackLegalPageViewed = (pageType: 'privacy' | 'terms' | 'cookie_policy' | 'ccpa') => {
  trackEvent('legal_page_viewed', { page_type: pageType });
};

export const trackSupportPageViewed = () => {
  trackEvent('support_page_viewed');
};

export const trackCookieSettingsViewed = () => {
  trackEvent('cookie_settings_viewed');
};

export const trackCookiePreferencesUpdated = (analytics: boolean, marketing: boolean) => {
  trackEvent('cookie_preferences_updated', { analytics, marketing });
};

export const trackStatusPageViewed = () => {
  trackEvent('status_page_viewed');
};

export const trackNotificationsPageViewed = (unreadCount: number) => {
  trackEvent('notifications_page_viewed', { unread_count: unreadCount });
};

export const trackPaymentReturnViewed = (status?: string, paymentId?: string) => {
  trackEvent('payment_return_viewed', {
    status: status || 'unknown',
    payment_id: paymentId || 'unknown',
  });
};

declare global {
  interface Window {
    GA_MEASUREMENT_ID?: string;
  }
}
